import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Trash2, ShoppingBag, Plus, Minus } from 'lucide-react';
import { getBulkDiscount } from '../utils/pricing';

export const Cart = () => {
  const { cart, removeFromCart, updateQuantity, getTotalAmount } = useCart();

  const getOrderTypeLabel = (orderType, quantity) => {
    switch (orderType) {
      case 'stems':
        return `${quantity} ${quantity === 1 ? 'stem' : 'stems'}`;
      case 'subscription-weekly':
        return 'Weekly Subscription';
      case 'subscription-monthly':
        return 'Monthly Subscription';
      default:
        return `${quantity} stems`;
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-brand-cream pt-24 pb-16 px-6 flex items-center justify-center" data-testid="cart-page">
        <div className="text-center">
          <ShoppingBag className="w-16 h-16 text-brand-gold/50 mx-auto mb-6" strokeWidth={1.5} />
          <h2 className="font-heading text-3xl font-light text-brand-green-dark mb-4">Your Cart is Empty</h2>
          <p className="font-body text-brand-green-dark/70 mb-8">
            Discover our beautiful A-grade oriental lilies
          </p>
          <Link
            to="/products"
            className="inline-block bg-brand-green text-brand-cream px-10 py-3 rounded-sm font-body uppercase tracking-[0.2em] text-sm font-bold hover:bg-brand-gold hover:text-brand-green-dark smooth-transition"
            data-testid="continue-shopping-btn"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-cream pt-24 pb-16 px-6 fade-in" data-testid="cart-page">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="font-heading text-5xl font-light tracking-tight text-brand-green-dark mb-4">
            Your Cart
          </h1>
          <p className="font-body text-brand-green-dark/70">
            Review your selection before checkout
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {cart.map((item, index) => {
              const discount = item.orderType === 'stems' ? getBulkDiscount(item.quantity) : 0;
              return (
                <div
                  key={`${item.productId}-${item.orderType}`}
                  className="bg-brand-blush/20 gold-border-glow p-6 flex gap-6 rounded-sm"
                  data-testid={`cart-item-${index}`}
                >
                  <div className="w-24 h-24 flex-shrink-0">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover gold-border-glow rounded-sm"
                    />
                  </div>

                  <div className="flex-1">
                    <h3 className="font-heading text-xl font-light text-brand-green-dark mb-1">
                      {item.name}
                    </h3>
                    <p className="font-body text-sm text-brand-gold mb-2">
                      {getOrderTypeLabel(item.orderType, item.quantity)}
                    </p>
                    {discount > 0 && (
                      <p className="font-body text-xs text-brand-green font-bold mb-2">
                        {discount}% bulk discount applied
                      </p>
                    )}

                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => updateQuantity(item.productId, item.orderType, item.quantity - 1)}
                        className="w-8 h-8 flex items-center justify-center border border-brand-gold/50 text-brand-gold hover:bg-brand-gold hover:text-brand-cream smooth-transition rounded-sm"
                        data-testid={`decrease-quantity-${index}`}
                      >
                        <Minus className="w-3 h-3" strokeWidth={1.5} />
                      </button>
                      <span className="text-brand-green-dark font-body w-8 text-center" data-testid={`item-quantity-${index}`}>
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.productId, item.orderType, item.quantity + 1)}
                        className="w-8 h-8 flex items-center justify-center border border-brand-gold/50 text-brand-gold hover:bg-brand-gold hover:text-brand-cream smooth-transition rounded-sm"
                        data-testid={`increase-quantity-${index}`}
                      >
                        <Plus className="w-3 h-3" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>

                  <div className="text-right flex flex-col justify-between">
                    <p className="font-body text-brand-green-dark text-lg" data-testid={`item-price-${index}`}>
                      ₹{item.price * item.quantity}
                    </p>
                    <button
                      onClick={() => removeFromCart(item.productId, item.orderType)}
                      className="text-brand-green-dark/50 hover:text-brand-gold smooth-transition"
                      data-testid={`remove-item-${index}`}
                    >
                      <Trash2 className="w-5 h-5" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="lg:col-span-1">
            <div className="bg-brand-green p-8 rounded-sm sticky top-24">
              <h2 className="font-heading text-2xl font-light text-brand-cream mb-6">
                Order Summary
              </h2>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between font-body text-brand-cream/90">
                  <span>Subtotal</span>
                  <span data-testid="cart-subtotal">₹{getTotalAmount()}</span>
                </div>
                <div className="flex justify-between font-body text-brand-cream/90">
                  <span>Delivery</span>
                  <span className="text-brand-gold text-sm">Free</span>
                </div>
              </div>

              <div className="border-t border-brand-gold/40 pt-4 mb-6">
                <div className="flex justify-between font-heading text-2xl text-brand-cream">
                  <span>Total</span>
                  <span data-testid="cart-total">₹{getTotalAmount()}</span>
                </div>
              </div>

              <Link
                to="/checkout"
                className="block w-full bg-brand-gold text-brand-green-dark py-4 rounded-sm font-body uppercase tracking-[0.2em] text-sm font-bold hover:bg-brand-cream smooth-transition text-center mb-4"
                data-testid="proceed-to-checkout-btn"
              >
                Proceed to Checkout
              </Link>

              <Link
                to="/products"
                className="block w-full text-center text-brand-cream/80 hover:text-brand-gold font-body text-sm smooth-transition"
                data-testid="continue-shopping-link"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
